import { useNavigate } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2, Zap } from 'lucide-react';
import { useState } from 'react';

import {
  useDisableV2Mode,
  useEnableV2Mode,
  useGetV2ModeStatus,
} from '~entities/v2-mode/api/v2-mode-api';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Switch } from '~shared/shadcn/ui/switch';

export const V2ModeSettings = () => {
  const navigate = useNavigate();
  const { data: status, isLoading } = useGetV2ModeStatus();
  const enableMutation = useEnableV2Mode();
  const disableMutation = useDisableV2Mode();
  const [confirmAction, setConfirmAction] = useState<
    'enable' | 'disable' | null
  >(null);

  const isV2 = status?.isV2 ?? false;
  const isMutating = enableMutation.isPending || disableMutation.isPending;

  const handleToggle = (checked: boolean) => {
    setConfirmAction(checked ? 'enable' : 'disable');
  };

  const handleConfirm = () => {
    if (confirmAction === 'enable') {
      enableMutation.mutate();
    } else if (confirmAction === 'disable') {
      disableMutation.mutate();
    }
    setConfirmAction(null);
  };

  return (
    <div className='mx-auto max-w-3xl space-y-6 p-6'>
      <div className='flex items-center gap-3'>
        <Button
          variant='ghost'
          size='icon'
          onClick={() => navigate({ to: '/dashboard' })}
        >
          <CircleArrowLeft className='h-5 w-5' />
        </Button>
        <h1 className='text-2xl font-semibold'>V2 Mode</h1>
      </div>

      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : (
        <div className='space-y-6'>
          <div
            className={`rounded-lg p-6 transition-all duration-200 ${isV2 ? 'bg-amber-50 shadow-sm' : 'bg-gray-50'}`}
          >
            <div className='flex items-start justify-between gap-4'>
              <div className='space-y-2'>
                <div className='flex items-center gap-2'>
                  <Zap className='h-5 w-5 text-amber-600' />
                  <label className='text-lg font-medium'>
                    V2 Enrollment {isV2 ? '(Active)' : '(Inactive)'}
                  </label>
                </div>
                <p className='text-sm text-muted-foreground'>
                  When enabled, studio class enrollment (single and bulk) uses
                  the V2 preview and charge APIs. When disabled, enrollment uses
                  the legacy payment processor.
                </p>
                {isV2 && (
                  <p className='text-xs font-medium text-amber-600'>
                    V2 mode is active. Class enroll calls{' '}
                    <code>GET/POST /v2/enrollments</code>.
                  </p>
                )}
              </div>
              <Switch
                checked={isV2}
                onCheckedChange={handleToggle}
                disabled={isMutating}
              />
            </div>
          </div>

          <div className='rounded-lg border bg-white p-6'>
            <h2 className='mb-3 text-base font-medium'>How it works</h2>
            <ul className='space-y-2 text-sm text-muted-foreground'>
              <li>
                <strong>Enabled</strong> — Proceed to Enrollment runs V2
                preview, then Confirm starts V2 enrollment.
              </li>
              <li>
                <strong>Disabled</strong> — Enrollment uses legacy{' '}
                <code>/payment-processor/add-student-to-entity</code> (default).
              </li>
              <li>
                Event enrollment is unchanged and still uses the legacy path.
              </li>
            </ul>
          </div>
        </div>
      )}

      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'enable'
                ? 'Enable V2 Mode?'
                : 'Disable V2 Mode?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'enable'
                ? 'Class enrollment (single and bulk) will switch to the V2 preview and charge APIs.'
                : 'Class enrollment will switch back to the legacy add-student-to-entity flow.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {confirmAction === 'enable' ? 'Enable' : 'Disable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
